import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import styles from "./toaster.module.css";

const ToasterContext = createContext(null);

let toastId = 1;

const ToasterProvider = ({ children, position = "bottom-right" }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(({ type = "info", message = "", duration = 4000 }) => {
    const id = toastId++;
    const toast = { id, type, message, duration };
    setToasts((prev) => [toast, ...prev]);

    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
    return id;
  }, [removeToast]);

  const posClassMap = {
    "top-right": styles.position_topRight,
    "bottom-right": styles.position_bottomRight,
    "top-left": styles.position_topLeft,
    "bottom-left": styles.position_bottomLeft,
    "bottom-center": styles.position_bottomCenter,
  };

  const positionClass = posClassMap[position] || styles.position_bottomRight;

  return (
    <ToasterContext.Provider value={{ showToast, removeToast }}>
      {children}
      <div className={styles.wrapper} aria-live="polite">
        <div className={`${styles.container} ${positionClass}`}>
          {toasts.map((t) => (
            <Toast key={t.id} toast={t} onClose={() => removeToast(t.id)} />
          ))}
        </div>
      </div>
    </ToasterContext.Provider>
  );
};

export const useToaster = () => {
  const ctx = useContext(ToasterContext);
  if (!ctx) throw new Error("useToaster must be used within ToasterProvider");
  return ctx;
};

const Toast = ({ toast, onClose }) => {
  const { id, type, message } = toast;
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // trigger enter animation
    requestAnimationFrame(() => setVisible(true));
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleClose = () => {
    setVisible(false);
    // wait for animation to finish before removing
    setTimeout(() => onClose(id), 250);
  };

  return (
    <div
      className={`${styles.toast} ${styles[`type_${type}`]} ${
        visible ? styles.enter : styles.exit
      } font-instrument`}
      role="status"
    >
      <div className={styles.message}>{message}</div>
      <button className={styles.close} onClick={handleClose} aria-label="Close">
        ×
      </button>
    </div>
  );
};

export default ToasterProvider;
